import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Heading,
  Stack,
  Text,
  useToast,
  VStack,
  ScaleFade,
  Image,
  Flex,
  Spinner
} from '@chakra-ui/react';
import useAuth from '../store/useAuth';
import useWallet, { WALLET_STATUS } from '../store/useWallet';
import { COLORS, ROUTES } from '../utils/constants';
import { ExternalLinkIcon } from '@chakra-ui/icons';

// Logo with animation
const Logo = () => {
  return (
    <Box 
      bg="transparent" 
      textAlign="center"
      position="relative"
      mt={0}
    >
      <Box 
        display="flex" 
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        position="relative"
      >
        {/* Glow effect behind logo */}
        <Box
          position="absolute"
          width="80px"
          height="80px"
          borderRadius="full"
          bg={`radial-gradient(circle, ${COLORS.primary}30 0%, transparent 70%)`}
          filter="blur(25px)"
          opacity="0.7"
          zIndex="0"
        />
        
        {/* Main logo without border */}
        <Image 
          src="/IgnitaLogo.svg" 
          alt="Ignita Logo" 
          height="80px" 
          zIndex="1"
          position="relative"
          filter="drop-shadow(0 0 10px rgba(0, 200, 80, 0.5))"
        />
        
        {/* Text with glow */}
        <Text 
          color={COLORS.textSecondary} 
          fontSize="md"
          fontWeight="medium"
          letterSpacing="wide"
          mt={1}
          position="relative"
          zIndex="1"
          textShadow="0 0 8px rgba(0, 200, 80, 0.2)"
        >
          AI Innovation Platform
        </Text>
      </Box>
    </Box>
  );
};

// Coming Soon Component
/*
const ComingSoonBanner = () => {
  return (
    <Box
      bg="rgba(0, 0, 0, 0.6)"
      borderRadius="lg"
      p={6}
      mb={6}
      borderWidth="1px"
      borderColor={COLORS.primary}
      boxShadow={`0 0 20px ${COLORS.primary}30`}
      position="relative"
      overflow="hidden"
    >
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        bgGradient={`radial-gradient(circle, ${COLORS.primary}20 0%, transparent 70%)`}
        opacity="0.6"
        animation="pulse 4s infinite"
        sx={{
          "@keyframes pulse": {
            "0%": { opacity: 0.2 },
            "50%": { opacity: 0.6 },
            "100%": { opacity: 0.2 }
          }
        }}
      />
      
      <Heading
        as="h2"
        size="xl"
        textAlign="center"
        bgGradient={GRADIENTS.primary}
        bgClip="text"
        letterSpacing="wider"
        mb={3}
        position="relative"
      >
        COMING SOON
      </Heading>
      
      <Text
        color={COLORS.text}
        textAlign="center"
        fontSize="md"
        position="relative"
      >
        The Ignita platform is currently in development.
        Stay tuned for our official launch!
      </Text>
    </Box>
  );
};
*/

function Login() {
  const [isLoading, setIsLoading] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  const { login, walletLogin } = useAuth();
  const { initialize, connectXaman, status, address, errorMessage } = useWallet();
  const [qrCodeUrl, setQrCodeUrl] = useState('');
  const [deepLink, setDeepLink] = useState('');

  // Animation on mount and auto-initialize XAMAN connection
  useEffect(() => {
    setIsVisible(true);
    initialize(); // Initialize the wallet store
    
    // Auto-initialize XAMAN connection
    const initializeXamanConnection = async () => {
      setIsLoading(true);
      
      try {
        const result = await connectXaman({ autoOpenPopup: false });
        
        if (result.success) {
          setQrCodeUrl(result.qrUrl);
          setDeepLink(result.deepLink);
          
          toast({
            title: 'XAMAN Ready',
            description: 'Scan the QR code with your XUMM app to sign in.',
            status: 'info',
            duration: 7000,
            isClosable: true,
            position: 'top',
          });
        } else {
          throw new Error(result.error?.message || 'Failed to initialize XAMAN connection.');
        }
      } catch (error) {
        console.error("XAMAN initialization error:", error);
        toast({
          title: 'XAMAN Connection Failed',
          description: error.message || 'An unexpected error occurred. Please refresh the page.',
          status: 'error',
          duration: 7000,
          isClosable: true,
          position: 'top',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    initializeXamanConnection();
    
    // Clean up resources when component unmounts
    return () => {
      useWallet.getState().cleanup();
    };
  }, [initialize, connectXaman, toast]);

  // Wrap handleWalletLoginSuccess in useCallback to prevent unnecessary re-renders
  const handleWalletLoginSuccess = useCallback(async (walletAddress) => {
    try {
      // Login with the connected wallet address
      await login({ xaman: true, walletAddress });
      
      console.log("Login successful - redirecting to generate page");
      
      toast({
        title: 'XAMAN login successful',
        description: `Connected to wallet: ${walletAddress.substring(0, 10)}...${walletAddress.substring(walletAddress.length - 6)}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      
      // Force completion of loading state
      setIsLoading(false);
      
      // Redirect to Generate page
      navigate(ROUTES.GENERATE);
    } catch (error) {
      console.error("Error during wallet login:", error);
      
      toast({
        title: 'XAMAN login failed',
        description: error.message || 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top',
      });
    } finally {
      setIsLoading(false);
    }
  }, [login, navigate, toast]);

  // Monitor wallet connection status
  useEffect(() => {
    console.log("Wallet status changed:", status, "Address:", address);
    
    if (status === WALLET_STATUS.CONNECTED && address) {
      console.log("Wallet connected successfully - proceeding with login");
      // Once wallet is connected, proceed with login
      handleWalletLoginSuccess(address);
    } else if (status === WALLET_STATUS.ERROR) {
      setIsLoading(false);
    }
  }, [status, address, handleWalletLoginSuccess]);

  return (
    <Flex 
      minHeight="100vh" 
      width="100%" 
      align="center" 
      justify="center"
      position="relative"
      py={6}
    >
      {/* Additional background hexagons to replace middle dots */}
      <Box 
        position="absolute" 
        top="10%" 
        left="15%" 
        width="80px"
        height="80px"
        opacity="0.12"
        zIndex="1"
      >
        <svg viewBox="0 0 100 100">
          <path 
            d="M50 0 L93.3 25 L93.3 75 L50 100 L6.7 75 L6.7 25 Z" 
            fill="none" 
            stroke={COLORS.primary} 
            strokeWidth="1.5"
          />
        </svg>
      </Box>
      
      <Box 
        position="absolute" 
        bottom="10%" 
        right="15%" 
        width="100px"
        height="100px"
        opacity="0.08"
        zIndex="1"
      >
        <svg viewBox="0 0 100 100">
          <path 
            d="M50 0 L93.3 25 L93.3 75 L50 100 L6.7 75 L6.7 25 Z" 
            fill="none" 
            stroke={COLORS.primary} 
            strokeWidth="1.5"
          />
        </svg>
      </Box>
      
      <Container maxW="md" centerContent>
        <ScaleFade initialScale={0.95} in={isVisible}>
          <VStack spacing={4} w="100%">
            <Logo />

            {/* Login Card with fixed styling - no hover animation */}
            <Box 
              w="100%" 
              maxW="450px"
              minW={["auto", "380px"]}
              bg="rgba(2, 10, 2, 0.85)" 
              p={[5, 7]} 
              borderRadius="lg" 
              boxShadow="0 10px 30px rgba(0, 0, 0, 0.4)"
              backdropFilter="blur(12px)"
              borderWidth="1px"
              borderColor={`${COLORS.primary}40`}
              position="relative"
              zIndex="10"
              overflow="hidden"
              transition="none"
              mx="auto"
            >
              {/* Card highlight effects */}
              <Box 
                position="absolute" 
                top="0" 
                left="0" 
                right="0" 
                height="2px" 
                bgGradient={`linear(to-r, transparent, ${COLORS.primary}, transparent)`}
                opacity="0.7"
              />
              
              <Stack spacing={5} width="100%">
                <Heading 
                  size="md" 
                  textAlign="center" 
                  mb={1}
                  letterSpacing="wide"
                  fontWeight="600"
                  color={COLORS.text}
                >
                  Enter the World of Ignita
                </Heading>
                
                <VStack spacing={4} align="center">
                  {status === WALLET_STATUS.ERROR ? (
                    <Box 
                      p={4} 
                      bg="rgba(255, 0, 0, 0.1)" 
                      borderRadius="md" 
                      borderWidth="1px" 
                      borderColor="red.500"
                      width="100%"
                    >
                      <Text color="red.400" fontWeight="bold" mb={2}>Connection Error</Text>
                      <Text fontSize="sm">{errorMessage || "Failed to connect to Xaman. Please check your configuration or try again."}</Text>
                      <Button 
                        mt={3} 
                        size="sm" 
                        onClick={() => window.location.reload()}
                        colorScheme="red"
                        variant="outline"
                      >
                        Retry
                      </Button>
                    </Box>
                  ) : (
                    <>
                      <Text fontSize="sm" textAlign="center" color={COLORS.textSecondary}>
                        {status === WALLET_STATUS.CONNECTING ? 
                          (window.ENABLE_XAMAN_TEST_MODE ? "Test Mode: Xaman window opened. Auto-connecting..." : "Scan QR code or complete sign-in in the Xaman window.") :
                          "Preparing Xaman connection..."}
                      </Text>
                      
                      {qrCodeUrl ? (
                        <Box 
                          borderRadius="md" 
                          overflow="hidden" 
                          p={4} 
                          bg="white" 
                          boxShadow="0 0 20px rgba(30, 220, 11, 0.3)"
                          textAlign="center"
                        >
                          <Image 
                            src={qrCodeUrl} 
                            alt={window.ENABLE_XAMAN_TEST_MODE ? "Xaman Logo" : "Xaman Sign-In QR Code"} 
                            maxW="200px" 
                            mx="auto" 
                          />
                          {window.ENABLE_XAMAN_TEST_MODE ? (
                            <Text mt={3} fontSize="xs" color="black">Test Mode: Auto-login in progress.</Text>
                          ) : (
                            <Text mt={3} fontSize="xs" color="black">Scan with Xaman or use the button below.</Text>
                          )}
                        </Box>
                      ) : (
                        <Box height="200px" display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                          <Spinner 
                            color={COLORS.primary} 
                            size="xl" 
                            thickness="4px"
                            speed="0.8s"
                          />
                          <Text mt={3} fontSize="sm" color="gray.400">Loading Xaman connection...</Text>
                        </Box>
                      )}
                      
                      {deepLink && !window.ENABLE_XAMAN_TEST_MODE && (
                        <Button
                          leftIcon={<ExternalLinkIcon />}
                          colorScheme="green"
                          bg={COLORS.primary}
                          color="black"
                          size="md"
                          width="100%"
                          onClick={() => {
                            const targetUrl = window.xummSignWindowUrl || deepLink;
                            const xamanWin = window.open(targetUrl, '_blank', 'width=450,height=700,noopener,noreferrer');
                            if (!xamanWin) {
                                toast({ title: "Popup Blocked", description: "Please allow popups for this site to open the Xaman window.", status: "warning", duration: 5000 });
                            }
                          }}
                          mt={2}
                          isLoading={isLoading}
                          loadingText="Connecting..."
                        >
                          Open Xaman Window
                        </Button>
                      )}
                      
                      {/* <Text fontSize="xs" color="gray.400" mt={2} textAlign="center">
                        {status === WALLET_STATUS.CONNECTING ? 
                          (window.ENABLE_XAMAN_TEST_MODE ? "Please wait for auto-connection..." : "Waiting for you to complete the sign-in...") :
                          "Ready to connect with your XRPL wallet."}
                      </Text> */}
                    </>
                  )}
                </VStack>
              </Stack>
            </Box>
          </VStack>
        </ScaleFade>
      </Container>
    </Flex>
  );
}

export default Login; 